(() => {
  'use strict';
  const initialize = (root) => {
    if (root.dataset.rccReady) return;
    root.dataset.rccReady = 'true';
    const form = root.querySelector('[data-rcc-form]');
    if (!form) return;
    const copy = JSON.parse(root.querySelector('[data-rcc-strings]').textContent);
    const model = root.querySelector('[data-rcc-model]');
    const condition = root.querySelector('[data-rcc-condition]');
    const inStock = root.querySelector('[data-rcc-instock]');
    const button = root.querySelector('[data-rcc-submit]');
    const status = root.querySelector('[data-rcc-status]');
    const results = root.querySelector('[data-rcc-results]');
    const cards = root.querySelector('[data-rcc-result-cards]');
    const title = root.querySelector('[data-rcc-results-title]');
    const cache = new Map();
    let controller;
    let sequence = 0;
    let loading;
    const money = (value) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: root.dataset.currency || 'USD' }).format(Number(value));
    const element = (tag, text, className) => {
      const node = document.createElement(tag);
      if (text) node.textContent = text;
      if (className) node.className = className;
      return node;
    };
    const partOf = (product) => {
      const text = product.title.toLowerCase();
      if (/full set|complete set|protective|silicone|transparent|ear tips|earpads|lanyard|cover|shell/.test(text)) return '';
      if (/\bleft\b/.test(text)) return 'left';
      if (/\bright\b/.test(text)) return 'right';
      if (/charging case|case replacement/.test(text)) return 'case';
      return '';
    };
    const conditionOf = (product, variant) => {
      const index = (product.options || []).findIndex((option) => /condition|grade/i.test(typeof option === 'string' ? option : option.name));
      if (index >= 0) return String(variant['option' + (index + 1)] || variant.options?.[index] || '');
      const text = product.title + ' ' + String(product.body_html || '').replace(/<style[\s\S]*?<\/style>|<script[\s\S]*?<\/script>/gi, '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;|\s+/g, ' ');
      if (/heavy wear/i.test(product.title) || /condition:\s*eco friendly[^.]*heavy wear/i.test(text)) return copy.heavyWear;
      if (/slightly used/i.test(product.title) || /condition:\s*slightly used/i.test(text)) return copy.slightlyUsed;
      if (/condition:\s*(?:open box\s*)?like[- ]new/i.test(text)) return copy.likeNew;
      return '';
    };
    const refreshConditions = () => {
      const previous = condition.value;
      const selectedPart = form.querySelector('input[type="radio"]:checked')?.value || 'all';
      const products = (cache.get(model.value) || []).filter((product) => partOf(product) && (selectedPart === 'all' || partOf(product) === selectedPart));
      const values = [...new Set(products.flatMap((product) => product.variants.filter((variant) => !inStock.checked || variant.available).map((variant) => conditionOf(product, variant))).filter(Boolean))];
      const rank = (name) => /like[- ]new|open box/i.test(name) ? 0 : /excellent/i.test(name) ? 1 : /good|slightly used/i.test(name) ? 2 : /fair|heavy wear/i.test(name) ? 3 : 4;
      condition.replaceChildren(new Option(copy.allConditions, ''));
      values.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)).forEach((value) => condition.add(new Option(value, value)));
      if (values.includes(previous)) condition.value = previous;
    };
    const imageOf = (product, variant) => variant?.featured_image?.src || product.images?.[0]?.src || '';
    const getCatalog = async (url, signal) => {
      if (cache.has(url)) return cache.get(url);
      const endpoint = new URL(url, location.origin);
      if (endpoint.origin !== location.origin || !endpoint.pathname.includes('/collections/')) throw new Error('Invalid collection');
      endpoint.pathname = endpoint.pathname.replace(/\/$/, '') + '/products.json';
      let products = [];
      for (let page = 1; page <= 20; page += 1) {
        endpoint.search = new URLSearchParams({ limit: '250', page: String(page) }).toString();
        const response = await fetch(endpoint, { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection unavailable');
        const data = await response.json();
        if (!Array.isArray(data.products)) throw new Error('Invalid collection data');
        products = products.concat(data.products);
        if (data.products.length < 250) { cache.set(url, products); return products; }
      }
      throw new Error('Collection too large');
    };
    const clearResults = () => {
      results.hidden = true;
      const native = document.getElementById('CollectionAjaxResult');
      if (native) native.hidden = false;
    };
    const showError = () => {
      status.replaceChildren(element('span', copy.error + ' '));
      if (model.value) { const link = element('a', copy.browse); link.href = model.value; status.append(link); }
    };
    const loadModel = async () => {
      const current = ++sequence;
      if (controller) controller.abort();
      controller = new AbortController();
      const request = controller;
      const signal = request.signal;
      clearResults();
      status.textContent = '';
      condition.replaceChildren(new Option(copy.allConditions, ''));
      root.querySelectorAll('[data-rcc-part-image]').forEach((image) => { image.hidden = true; image.removeAttribute('src'); });
      if (!model.value) { button.disabled = false; condition.disabled = false; return []; }
      const selectedURL = model.value;
      button.disabled = true;
      condition.disabled = true;
      status.textContent = copy.loading;
      const timer = setTimeout(() => request.abort(), 15000);
      try {
        const products = (await getCatalog(selectedURL, signal)).filter((product) => partOf(product));
        if (current !== sequence) return [];
        refreshConditions();
        root.querySelectorAll('[data-rcc-part-image]').forEach((image) => {
          const product = products.find((item) => partOf(item) === image.dataset.rccPartImage && item.variants.some((variant) => variant.available)) || products.find((item) => partOf(item) === image.dataset.rccPartImage);
          const source = product && imageOf(product, product.variants.find((variant) => variant.available));
          if (source) { image.src = source; image.hidden = false; }
        });
        status.textContent = '';
        return products;
      } catch (error) {
        if (current === sequence && !signal.aborted) showError();
        else if (current === sequence) showError();
        return null;
      } finally {
        clearTimeout(timer);
        if (current === sequence) { button.disabled = false; condition.disabled = false; }
      }
    };
    model.addEventListener('change', () => { loading = loadModel(); });
    form.addEventListener('change', (event) => { if (event.target !== model) { clearResults(); status.textContent = ''; if (event.target.matches('input[type="radio"]') || event.target === inStock) refreshConditions(); } });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const current = sequence;
      if (loading) await loading;
      if (current !== sequence) return;
      const products = cache.get(model.value);
      if (!products) { showError(); return; }
      const part = form.querySelector('input[type="radio"]:checked')?.value || 'all';
      const matches = products.filter((product) => partOf(product) && (part === 'all' || partOf(product) === part)).map((product) => {
        const variants = product.variants.filter((variant) => (!condition.value || conditionOf(product, variant) === condition.value) && (!inStock.checked || variant.available));
        const available = variants.filter((variant) => variant.available);
        const candidates = (available.length ? available : variants).slice().sort((a, b) => Number(a.price) - Number(b.price));
        return { product, variant: candidates[0] };
      }).filter((item) => item.variant);
      cards.replaceChildren();
      matches.forEach(({ product, variant }) => {
        const card = element('article', '', 'rcc-product-card');
        const url = new URL((root.dataset.root || '/') + 'products/' + product.handle, location.origin);
        url.searchParams.set('variant', variant.id);
        const photo = element('a', '', 'rcc-product-photo');
        photo.href = url.href;
        photo.setAttribute('aria-label', product.title);
        const source = imageOf(product, variant);
        if (source) { const image = document.createElement('img'); image.src = source; image.alt = product.title; image.width = 400; image.height = 300; image.loading = 'lazy'; photo.append(image); }
        const body = element('div', '', 'rcc-product-body');
        const heading = element('h3');
        const link = element('a', product.title); link.href = url.href; heading.append(link);
        body.append(heading, element('p', money(variant.price), 'rcc-card-price'));
        if (variant.title && variant.title !== 'Default Title') body.append(element('p', variant.title, 'rcc-card-option'));
        const grade = conditionOf(product, variant);
        if (grade && !String(variant.title).includes(grade)) body.append(element('p', grade, 'rcc-card-option'));
        body.append(element('p', variant.available ? copy.available : copy.soldOut, 'rcc-card-stock' + (variant.available ? '' : ' is-sold-out')));
        const cta = element('a', variant.available ? copy.choose : copy.view, 'rcc-card-cta'); cta.href = url.href; cta.append(element('span', '→')); body.append(cta);
        card.append(photo, body); cards.append(card);
      });
      title.textContent = model.selectedOptions[0].textContent + ' · ' + copy.matches;
      status.textContent = matches.length ? String(matches.length) + ' ' + copy.ready : copy.empty;
      if (!matches.length) cards.append(element('p', copy.empty));
      results.hidden = false;
      const native = document.getElementById('CollectionAjaxResult');
      if (native) native.hidden = true;
      title.focus({ preventScroll: true });
      results.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    });
    loading = loadModel();
  };
  const init = (root) => root.querySelectorAll('[data-rcc]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
